import React from "react";
import "./MenuUser.css";
import { Box, IconButton, Menu, Typography } from "@mui/material";
import MenuIcon from "@mui/material/Menu";
import MenuListHeader from "../MenuListHeader/MenuListHeader";
import useAxios from "../../Hooks/useAxios";
import Divider from '@mui/material/Divider';
import { GlobalContext } from "../../Context/GlobalContext";



/**
 * A user menu component for the header that displays a dropdown menu with user-related options and details.
 * The menu is triggered by an icon button and includes user information fetched from an API.
 * 
 * @component
 * @example
 * import MenuUser from "../../Components/MenuUser/MenuUser";
 * <MenuUser menuList={menuApi} />
 * @param {Array} menuList - A list of menu items to display in the dropdown. This data typically comes from an API.
 * @returns {React.Fragment} The rendered user menu component.
 */
function MenuUser({ menuList }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);  
  const { data, isLoading, error } = useAxios("employeeDetail");
  const { setUserProperties, setAlertBarSeverity, setAlertBarMessage, setAlertBarOpen } = React.useContext(GlobalContext);

  /**
   * Fetches user details from the API and updates the global context with user properties.
   */
  React.useEffect(() => {
    if (!isLoading){
      if (error) {
        setAlertBarSeverity("error");
        setAlertBarMessage(error);
        setAlertBarOpen(true);
      } else {
        setUserProperties({
          id: data.id,
          departmentId: data.departmentId,   
          viewProcesses: data.viewProcesses,
          modifyProjectDate: data.modifyProjectDate,
          createProcesses: data.createProcesses
        });


      }
    }
  }, [isLoading]);

  /**
   * Handles the click event to open or close the dropdown menu.
   * 
   * @param {Event} event - The click event.
   */
  const onClickMenuHandler = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  /**
   * Handles closing the dropdown menu.
   */
  const onCloseMenuHandler = () => {
    setAnchorEl(null);
  };

  /**
   * Renders the list of menu items using the `MenuListHeader` component.
   * 
   * @returns {Array} An array of `MenuListHeader` components.
   */
  const displayOptions = () => {
    return [
      ...menuList.map((item) => (
        <MenuListHeader item={item} key={item.label} afterClick={onCloseMenuHandler} />
      ))
    ];
  };

  /**
   * Displays the user's initials based on their full name.
   * 
   * @returns {string} The user's initials or "?" if data is loading or an error occurs.
   */
  const displayName = () => {
    if (isLoading || error) {
      return "?";
    }
    return data.fullName.slice(0,1) + data.fullName.split(" ")[1].slice(0,1);
  }



  
  /**
   * Displays the user's details (full name, department, and email) in the dropdown menu.
   * 
   * @returns {JSX.Element} The rendered user details or a loading/error message.
   */
  const userDetails = () => {
    if (isLoading) {
      return <p>Loading...</p>;
    }
    if (error){
      return error;
    }
    return (
      <Box sx={{padding: "8px 16px"}}>
        <Typography color="grey" fontSize="11px">{data.fullName}</Typography>
        <Typography color="grey" fontSize="11px">{data.department}</Typography>
        <Typography color="grey" fontSize="11px">{data.email}</Typography>
      </Box>
    );
  }

  return (

    <>
      <IconButton
        id="menu_button"
        size="medium"
        edge="end"
        color="inherit"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="header_menu"
        onClick={onClickMenuHandler}
        sx={{marginLeft: "auto"}}
      >{displayName()}
        <MenuIcon />
      </IconButton>
      <Menu
        id="header_menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onCloseMenuHandler}
        MenuListProps={{
          "aria-labelledby": "menu_button",
        }}
        sx={{ marginTop: "10px"}}
      >
      {displayOptions()}
      <Divider variant="middle" component="li" />
      {userDetails()}
      </Menu>
    </>
  );
}

export default MenuUser;
